import React, {useState} from "react";
import {FiAlertCircle, FiCheckCircle, FiMail, FiUser} from "react-icons/fi";
import {Link} from "react-router-dom";

const GAMES = require("../data/games.json");

function Unsubscribe() {
    const [email, setEmail] = useState("");
    const [userId, setUserId] = useState("");
    const [selectedGames, setSelectedGames] = useState([]);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    const [consent, setConsent] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleGameToggle = (gameId) => {
        setSelectedGames(prev =>
            prev.includes(gameId)
                ? prev.filter(id => id !== gameId)
                : [...prev, gameId]
        );
    };

    const handleSelectAll = () => {
        const allGameIds = Object.values(GAMES).map(game => game.id);
        const allSelected = allGameIds.length > 0 && allGameIds.every(id => selectedGames.includes(id));
        setSelectedGames(allSelected ? [] : allGameIds);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!email && !userId) {
            setError("Please provide either an email address or user ID.");
            return;
        }

        if (!consent) {
            setError("Please confirm your consent to unsubscribe.");
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {};
            if (email) payload.email = email;
            if (userId) payload.userId = userId;
            if (selectedGames.length > 0) payload.games = selectedGames;

            const response = await fetch('/api/unsubscribe', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                setDone(true);
            } else {
                setError("Failed to process unsubscribe request. Please try again or contact us directly.");
            }
        } catch (err) {
            console.error('Unsubscribe error:', err);
            setError("Failed to process unsubscribe request. Please try again or contact us directly.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="pt-24 pb-16 text-[#1a1a1a] min-h-screen">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-12 text-center">
                    <div className="inline-block mb-6">
                        <div
                            className="w-24 h-1 bg-gradient-to-r from-white via-white/80 to-white mx-auto rounded-full shadow-lg shadow-white/50"></div>
                    </div>
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <FiAlertCircle className="text-4xl md:text-5xl text-[#C8102E]"/>
                        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#C8102E] to-[#A00D25] bg-clip-text text-transparent drop-shadow-lg">
                            Unsubscribe
                        </h1>
                    </div>
                    <p className="text-[#2d1810] text-lg max-w-3xl mx-auto">
                        Account Deletion Request Confirmation and Policy
                    </p>
                </div>

                {!done ? (
                    <>
                        {/* Important Notice */}
                        <div
                            className="mb-8 bg-gradient-to-br from-yellow-500/20 via-yellow-400/20 to-yellow-500/20 rounded-2xl p-6 md:p-8 shadow-lg border border-yellow-400/40 backdrop-blur-sm">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-3 bg-yellow-500/20 rounded-lg border border-yellow-400/40">
                                    <FiAlertCircle className="text-yellow-700 text-2xl"/>
                                </div>
                                <h2 className="text-2xl font-bold text-[#78350f]">Important Notice: 10-Day Retention
                                    Period</h2>
                            </div>
                            <p className="text-[#78350f] mb-4 leading-relaxed">
                                A mandatory 10-day data retention window begins immediately.
                            </p>
                            <ul className="text-[#78350f] space-y-2">
                                <li className="flex items-start gap-2">
                                    <span className="text-yellow-700 mt-1">•</span>
                                    <span>During this time, you may cancel your deletion request at any moment through your account settings.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-yellow-700 mt-1">•</span>
                                    <span>If you choose to cancel, the deletion process will stop, and your account will remain active.</span>
                                </li>
                            </ul>
                        </div>

                        {/* Unsubscribe Form */}
                        <div
                            className="mb-8 bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-2xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                            <h2 className="text-2xl font-bold mb-6 text-white flex items-center gap-2">
                                <FiMail className="text-white"/>
                                Unsubscribe Form
                            </h2>

                            {error && (
                                <div
                                    className="mb-6 p-4 bg-red-700/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Email Input */}
                                <div>
                                    <label className="block text-sm font-medium text-white/90 mb-2">
                                        Email Address
                                    </label>
                                    <div className="relative">
                                        <FiMail
                                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white"/>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/20 border border-white/30 focus:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 transition text-white placeholder-white/70"
                                            placeholder="your.email@example.com"
                                        />
                                    </div>
                                    <p className="text-white/70 text-xs mt-1">Or provide your User ID below</p>
                                </div>

                                {/* User ID Input */}
                                <div>
                                    <label className="block text-sm font-medium text-white/90 mb-2">
                                        User ID
                                    </label>
                                    <div className="relative">
                                        <FiUser
                                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white"/>
                                        <input
                                            type="text"
                                            value={userId}
                                            onChange={(e) => setUserId(e.target.value)}
                                            className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/20 border border-white/30 focus:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 transition text-white placeholder-white/70"
                                            placeholder="Your User ID"
                                        />
                                    </div>
                                </div>

                                {/* Game Selection */}
                                {Object.keys(GAMES).length > 0 && (
                                    <div>
                                        <label className="block text-sm font-medium text-white/90 mb-3">
                                            Select Games to Unsubscribe From (Optional)
                                        </label>
                                        <div className="mb-3">
                                            <button
                                                type="button"
                                                onClick={handleSelectAll}
                                                className="text-sm px-4 py-2 bg-white/20 hover:bg-white/30 border border-white/30 rounded-lg transition text-white"
                                            >
                                                {Object.values(GAMES).every(game => selectedGames.includes(game.id)) ? 'Deselect All' : 'Select All'}
                                            </button>
                                        </div>
                                        <div
                                            className="max-h-60 overflow-y-auto bg-white/10 rounded-lg p-4 border border-white/20 scrollbar-hide">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {Object.values(GAMES).map((game) => (
                                                    <label
                                                        key={game.id}
                                                        className="flex items-center gap-3 p-3 bg-white/10 hover:bg-white/20 rounded-lg border border-white/20 cursor-pointer transition"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedGames.includes(game.id)}
                                                            onChange={() => handleGameToggle(game.id)}
                                                            className="w-4 h-4 text-[#C8102E] bg-white/20 border-white/30 rounded focus:ring-2 focus:ring-white/30"
                                                        />
                                                        <span className="text-white/90 text-sm">{game.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <p className="text-white/70 text-xs mt-2">
                                            Leave empty to unsubscribe from all games
                                        </p>
                                    </div>
                                )}

                                {/* Consent Checkbox */}
                                <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={consent}
                                            onChange={(e) => setConsent(e.target.checked)}
                                            className="mt-1 w-4 h-4 text-[#C8102E] bg-white/20 border-white/30 rounded focus:ring-2 focus:ring-white/30"
                                        />
                                        <span className="text-white/95 text-sm leading-relaxed">
                                            <strong className="text-white">Final Confirmation of Data Deletion</strong>
                                            <br/>
                                            By completing this form, you acknowledge and confirm that KidKat has your full consent to permanently erase all data associated with your account once the 10-day retention period has ended.
                                        </span>
                                    </label>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full px-6 py-3 bg-white hover:bg-white/90 disabled:bg-white/30 disabled:cursor-not-allowed rounded-lg transition font-bold flex items-center justify-center gap-2 shadow-lg shadow-white/30 text-[#C8102E]"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="animate-spin">⏳</span>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <FiAlertCircle/>
                                            Confirm Unsubscribe
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    </>
                ) : (
                    /* Success Message */
                    <div
                        className="bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-2xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm text-center">
                        <div className="inline-block p-4 bg-green-700/20 border border-green-500/30 rounded-full mb-4">
                            <FiCheckCircle className="w-12 h-12 text-green-400"/>
                        </div>
                        <h2 className="text-2xl font-bold mb-4 text-green-400">Request Submitted Successfully!</h2>
                        <p className="text-white/95 mb-6 leading-relaxed">
                            Thank you for submitting your request to delete your account. Your request has been
                            successfully added to our processing queue.
                        </p>
                        <div className="bg-white/10 rounded-lg p-4 border border-white/20 text-left mb-6">
                            <p className="text-white/90 text-sm">
                                <strong className="text-white">What happens next?</strong>
                            </p>
                            <ul className="text-white/80 text-sm mt-2 space-y-1">
                                <li>• Your account will be marked for deletion</li>
                                <li>• A 10-day retention period begins immediately</li>
                                <li>• You can cancel this request during the retention period</li>
                                <li>• After 10 days, your data will be permanently deleted</li>
                            </ul>
                        </div>
                        <Link
                            to="/"
                            className="inline-block px-6 py-3 bg-white hover:bg-white/90 rounded-lg transition font-bold text-[#C8102E] shadow-lg shadow-white/30"
                        >
                            Return to Home
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Unsubscribe;